import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { teachingView } from "@/lib/schedulingView";
import { SCHEDULER_VIEW_ENABLED } from "@/lib/featureFlags";
import { Button, Card, Eyebrow } from "@/components/studio/primitives";
import ClassSessionActions from "@/components/studio/ClassSessionActions";

/** A browser handoff, not an authorization grant. Resolve the current teacher's live view. */
export default function SchedulerLessonPage() {
  const [params] = useSearchParams(), navigate = useNavigate();
  const business = params.get("business"), appointment = params.get("appointment"), day = params.get("day");
  const [occurrence, setOccurrence] = useState(null), [error, setError] = useState(""), [loading, setLoading] = useState(true), [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setOccurrence(null); setError(""); setLoading(true);
    if (!SCHEDULER_VIEW_ENABLED || !business || !appointment || !/^\d{4}-\d{2}-\d{2}$/.test(day || "")) {
      setError("This lesson link is incomplete. Open the lesson from Scheduler again."); setLoading(false); return undefined;
    }
    // The link uses the business's date; the teaching view uses the instructor's
    // zone. Include adjacent dates, then match the exact authorized appointment.
    const anchor = new Date(`${day}T12:00:00Z`);
    if (!Number.isFinite(+anchor) || anchor.toISOString().slice(0, 10) !== day) {
      setError("This lesson link has an invalid date. Open it from Scheduler again."); setLoading(false); return undefined;
    }
    const from = new Date(+anchor - 2 * 86400000).toISOString().slice(0, 10);
    const to = new Date(+anchor + 2 * 86400000).toISOString().slice(0, 10);
    teachingView(from, to, { signal: controller.signal }).then(data => {
      if (controller.signal.aborted) return;
      const match = data?.occurrences?.find(row => row.business_id === business && row.scheduler_appointment_id === appointment);
      if (match) setOccurrence(match);
      else setError("This lesson is not available for your Hans instructor account. It may have moved, or its instructor or student connection may need updating in Scheduler.");
    }).catch(() => { if (!controller.signal.aborted) setError("The lesson could not be loaded. Try again."); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [business, appointment, day, retry]);
  return <div className="mx-auto max-w-3xl space-y-6 px-4 py-8 sm:px-6">
    <Eyebrow>From Kalver Scheduler</Eyebrow><h1 className="font-display text-3xl font-semibold text-[var(--text-primary)]">Your lesson</h1>
    <Card className="space-y-6 p-6 sm:p-8">{loading ? <p role="status" className="text-[var(--text-secondary)]">Opening your lesson…</p> : error ? <><p role="alert" className="text-[var(--text-secondary)]">{error}</p><Button variant="ghost" onPress={() => setRetry(value => value + 1)}>Try again</Button></> : <p className="text-[var(--text-secondary)]">View the lesson, write notes, or review its recording options.</p>}<Button variant="ghost" onPress={() => navigate("/dashboard")}>Back to Home</Button></Card>
    <ClassSessionActions occurrence={occurrence} onOpenChange={open => { if (!open) navigate("/dashboard"); }} />
  </div>;
}
